import type { ApiClient } from '@ghp/api-client'
import type { AssignedInquiry } from '@ghp/domain'

import type { DataDatabase } from './data-db'

/** Le sous-ensemble injecté du client : la relecture d'un média de l'équipe. */
export type CarriedMediaApi = Pick<ApiClient['sync'], 'downloadMedium'>

/**
 * Range sur la tablette les photos et pièces qu'une contre-visite reprend de la visite précédente
 * (#675), **avant que la fiche ne s'ouvre** : sur place, elle s'ouvre peut-être hors réseau.
 *
 * Le serveur les a déjà dupliquées dans le stockage, sous la clé de l'équipe et un identifiant
 * propre à la contre-visite : la tablette les range comme ses propres pièces, **déjà envoyées**
 * (`uploadedAt`), avec leur provenance (`carriedFrom`). Rien n'est partagé avec le contrôle
 * d'origine — retirer une pièce de la contre-visite ne touche que sa copie.
 *
 * ⚠️ **Tant que la fiche n'est pas ouverte, et seulement alors.** Une fois le brouillon créé, les
 * pièces appartiennent à l'agent : en remettre une qu'il a retirée défairait son geste. Avant, la
 * liste suit la tournée — une pièce que le serveur ne sert plus (la saisie reprise a changé) se
 * retire.
 *
 * ⚠️ **Une pièce qui ne descend pas ne fait pas échouer la descente** : la tournée est déjà rangée,
 * et la passe suivante reprend ce qui manque.
 */
export async function storeCarriedMedia(
  db: DataDatabase,
  api: CarriedMediaApi,
  accessToken: string,
  inquiries: readonly AssignedInquiry[],
): Promise<void> {
  for (const inquiry of inquiries) {
    if (inquiry.followUpSource === null) continue
    if ((await db.drafts.get(inquiry.id)) !== undefined) continue

    const carried = inquiry.followUpSource.media
    const served = new Set(carried.map((medium) => medium.id))
    const stale = await db.media
      .where('inquiryId')
      .equals(inquiry.id)
      .filter((medium) => medium.carriedFrom != null && !served.has(medium.id))
      .primaryKeys()
    await db.media.bulkDelete(stale)

    for (const medium of carried) {
      if ((await db.media.get(medium.id)) !== undefined) continue

      let blob: Blob
      try {
        blob = await api.downloadMedium(accessToken, medium.id)
      } catch (error) {
        console.warn(
          `[sync] la pièce ${medium.id} de la contre-visite ${inquiry.id} n'a pas pu descendre — reprise à la passe suivante.`,
          error,
        )
        continue
      }

      await db.media.put({
        id: medium.id,
        inquiryId: inquiry.id,
        blob,
        type: medium.type,
        takenAt: medium.takenAt,
        label: medium.label,
        uploadedAt: new Date().toISOString(),
        carriedFrom: medium.carriedFrom,
      })
    }
  }
}

/**
 * Retire les pièces reprises des contre-visites que la tournée **complète** ne sert plus, quand
 * l'agent ne les a jamais ouvertes (#675) — une réaffectation, un abandon, une remise à zéro.
 *
 * Elles ne sont la saisie de personne : une copie du serveur, qui se redescend si la demande
 * revient. Gardées, ce seraient des photos de propriété privée sans terme sur la tablette, et une
 * « pièce » qui retiendrait la demande comme une saisie de l'agent — le ménage des demandes non
 * servies (#668) garde toute demande qui porte un média.
 *
 * ⚠️ **Avant ce ménage-là**, donc : une fois retirées, rien ne compte plus la demande comme
 * touchée. Une fiche ouverte garde ses pièces — elles sont alors à l'agent (#147).
 *
 * ⚠️ Sur une tournée **complète** seulement : une page qui manque porte peut-être la demande.
 * Seules les demandes candidates sont lues, jamais toutes les pièces de la tablette.
 */
export async function dropUnservedCarriedMedia(
  db: DataDatabase,
  served: ReadonlySet<string>,
): Promise<void> {
  await db.transaction('rw', [db.drafts, db.media], async () => {
    const opened = new Set(await db.drafts.toCollection().primaryKeys())
    const withMedia = (await db.media.orderBy('inquiryId').uniqueKeys()) as string[]
    const candidates = withMedia.filter((id) => !served.has(id) && !opened.has(id))
    if (candidates.length === 0) return

    const carried = await db.media
      .where('inquiryId')
      .anyOf(candidates)
      .filter((medium) => medium.carriedFrom != null)
      .primaryKeys()
    await db.media.bulkDelete(carried)
  })
}
